import { useEffect, useState } from "react";
import { cn } from "@/lib/utils";
import { AISparkles } from "@/components/ui/ai-sparkles";

export type CountBadgeSize = "sm" | "default";

interface CountBadgeProps {
  count: number;
  /** `sm` (16px) for dense inline use, `default` (18px) elsewhere. */
  size?: CountBadgeSize;
  className?: string;
  /** Plays the count-pop + sparkle celebration once, e.g. after a prioritize lands. */
  celebrate?: boolean;
  /** Called once the sparkle finishes and is removed. */
  onCelebrateEnd?: () => void;
}

const sizeClasses: Record<CountBadgeSize, string> = {
  sm: "h-4 min-w-4 text-[11px]",
  default: "h-[1.125rem] min-w-[1.125rem] text-xs",
};

function prefersReducedMotion(): boolean {
  if (typeof window === "undefined" || typeof window.matchMedia !== "function") {
    return false;
  }
  return window.matchMedia("(prefers-reduced-motion: reduce)").matches;
}

/**
 * Small circular number badge. Use instead of parenthesised counts
 * (e.g. "More (12)" → "More" + <CountBadge count={12} />).
 */
export function CountBadge({
  count,
  size = "default",
  className,
  celebrate = false,
  onCelebrateEnd,
}: CountBadgeProps) {
  const [showSparkle, setShowSparkle] = useState(celebrate && !prefersReducedMotion());

  useEffect(() => {
    if (!celebrate) {
      return;
    }
    if (prefersReducedMotion()) {
      // No sparkle to animate, so there is no `animationend` to fire onCelebrateEnd —
      // the caller's celebrating state must be released here instead.
      setShowSparkle(false);
      onCelebrateEnd?.();
      return;
    }
    setShowSparkle(true);
  }, [celebrate]);

  if (!celebrate) {
    return (
      <span
        className={cn(
          "inline-flex items-center justify-center rounded-full bg-primary-tint-strong px-1 font-medium tabular-nums leading-none text-foreground",
          sizeClasses[size],
          className,
        )}
      >
        {count}
      </span>
    );
  }

  return (
    <span className="relative inline-flex">
      <span
        className={cn(
          "inline-flex items-center justify-center rounded-full bg-primary-tint-strong px-1 font-medium tabular-nums leading-none text-foreground",
          sizeClasses[size],
          "motion-safe:animate-count-pop",
          className,
        )}
      >
        {count}
      </span>
      {showSparkle && (
        <span
          aria-hidden="true"
          className="pointer-events-none absolute -right-1.5 -top-1.5 motion-safe:animate-sparkle-pop"
          onAnimationEnd={() => {
            setShowSparkle(false);
            onCelebrateEnd?.();
          }}
        >
          <AISparkles className="h-3 w-3" />
        </span>
      )}
    </span>
  );
}
